import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, fmt } from "../lib/api";
import { Flame } from "lucide-react";
import { toast } from "sonner";

const DIFFICULTY_LABELS = {
  easy: "Easy · 4×1",
  medium: "Medium · 3×1",
  hard: "Hard · 2×1",
  expert: "Expert · 3×2",
  master: "Master · 4×3",
};

export default function DragonTower() {
  const { user, currency, refresh } = useApp();
  const [searchParams] = useSearchParams();
  const [bet, setBet] = useState(currency === "HUF" ? 500 : 1);
  const [difficulty, setDifficulty] = useState(() => {
    const p = searchParams.get("difficulty");
    return ["easy","medium","hard","expert","master"].includes(p) ? p : "easy";
  });
  const [round, setRound] = useState(null); // {round_id, level, multiplier, next_multiplier, config, picks}
  const [allDragons, setAllDragons] = useState(null); // reveal on lose/cash
  const [busy, setBusy] = useState(false);
  const [payout, setPayout] = useState(0);
  const [lostAt, setLostAt] = useState(null);

  const start = async () => {
    setBusy(true);
    setAllDragons(null);
    setLostAt(null);
    setPayout(0);
    try {
      const r = await api.post("/instant/dragon/start", {
        bet: Number(bet), currency, difficulty,
      });
      setRound({ ...r.data, state: "running", picks: [], current_level: 0, multiplier: 1.00 });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const pickTile = async (tile) => {
    if (!round || round.state !== "running" || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/instant/dragon/pick", { round_id: round.round_id, tile });
      if (r.data.state === "lost") {
        setRound({ ...round, state: "lost", picks: r.data.picks });
        setAllDragons(r.data.all_dragon_positions);
        setLostAt({ level: r.data.level, tile: r.data.hit_tile });
        toast.error("🔥 Dragon! Lost.");
        refresh();
      } else if (r.data.state === "cashed") {
        setRound({ ...round, state: "cashed", picks: r.data.picks, current_level: r.data.level, multiplier: r.data.multiplier });
        setAllDragons(r.data.all_dragon_positions);
        setPayout(r.data.payout);
        toast.success(`Reached top! ${r.data.multiplier}x → ${fmt(r.data.payout, currency)}`);
        refresh();
      } else {
        setRound({ ...round, picks: r.data.picks, current_level: r.data.level, multiplier: r.data.multiplier, next_multiplier: r.data.next_multiplier });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const cashout = async () => {
    if (!round || round.state !== "running") return;
    setBusy(true);
    try {
      const r = await api.post("/instant/dragon/cashout", { round_id: round.round_id });
      setRound({ ...round, state: "cashed", multiplier: r.data.multiplier });
      setAllDragons(r.data.all_dragon_positions);
      setPayout(r.data.payout);
      toast.success(`Cashed ${r.data.multiplier}x → ${fmt(r.data.payout, currency)}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const cfg = round?.config;
  const levels = cfg?.levels || 8;
  const tilesPerRow = cfg?.tiles_per_row || 4;
  const isRunning = round?.state === "running";
  const isEnded = round?.state === "lost" || round?.state === "cashed";

  return (
    <div className="max-w-[1300px] mx-auto px-6 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Flame className="text-[#d4af37]" size={32}/>
        <h1 className="font-serif text-4xl">Dragon Tower</h1>
        <span className="text-xs text-white/40 uppercase tracking-widest ml-2">Climb without waking the dragon</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="lux-card p-6">
          {round ? (
            <div className="flex flex-col-reverse items-center gap-2">
              {Array.from({ length: levels }).map((_, lvl) => {
                const isCurrent = lvl === round.current_level && isRunning;
                const isPast = lvl < round.current_level;
                const pick = round.picks?.find(p=>p.level===lvl);
                const dragons = allDragons?.[lvl] || [];
                const isLostRow = lostAt && lostAt.level === lvl;
                return (
                  <div key={lvl} className={`flex gap-2 p-1 rounded ${isCurrent ? "ring-1 ring-[#d4af37]/40" : ""}`}>
                    <div className="text-xs text-white/40 w-14 text-right pt-2">L{lvl+1}</div>
                    {Array.from({ length: tilesPerRow }).map((_, t) => {
                      const isPicked = pick?.tile === t;
                      const isDragon = dragons.includes(t);
                      const showDragon = isEnded && isDragon;
                      return (
                        <button key={t} data-testid={`dragon-tile-${lvl}-${t}`}
                                disabled={!isCurrent || busy}
                                onClick={()=>pickTile(t)}
                                className={`w-14 h-14 rounded border transition flex items-center justify-center text-xl ${
                                  isPicked && !isLostRow ? "bg-emerald-900/60 border-emerald-400" :
                                  isPicked && isLostRow ? "bg-red-900/70 border-red-500" :
                                  showDragon ? "bg-red-900/40 border-red-500/60" :
                                  isCurrent ? "bg-[#1a1a1a] border-white/20 hover:border-[#d4af37]/60" :
                                  isPast ? "bg-black/60 border-white/5" : "bg-[#111] border-white/5 opacity-40"
                                }`}>
                          {isPicked && !isLostRow && "💎"}
                          {(isPicked && isLostRow) || showDragon ? "🐉" : ""}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              <div className="mt-4 text-center">
                {isRunning && (
                  <>
                    <div className="text-[#d4af37] font-serif text-3xl">{round.multiplier.toFixed(4)}x</div>
                    <div className="text-xs text-white/40">Next: {round.next_multiplier?.toFixed(4)}x</div>
                  </>
                )}
                {round?.state === "cashed" && (
                  <div className="text-emerald-400 font-bold">💎 Won {fmt(payout, currency)} at {round.multiplier.toFixed(2)}x</div>
                )}
                {round?.state === "lost" && (
                  <div className="text-red-500 font-bold">🔥 Dragon at L{lostAt?.level+1}</div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-20 text-white/40 text-sm">Set difficulty & bet, then start climbing.</div>
          )}
        </div>

        <div className="lux-card p-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Bet</label>
            <input data-testid="dragon-bet" type="number" value={bet} onChange={e=>setBet(e.target.value)} disabled={isRunning}
                   className="w-full bg-[#1a1a1a] border border-white/10 rounded px-3 py-3 font-serif text-xl focus:border-[#d4af37]"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-white/50 mb-1">Difficulty</label>
            <div className="grid grid-cols-1 gap-1">
              {Object.entries(DIFFICULTY_LABELS).map(([k,v])=>(
                <button key={k} data-testid={`dragon-diff-${k}`} disabled={isRunning}
                        onClick={()=>setDifficulty(k)}
                        className={`px-3 py-1.5 rounded text-xs ${difficulty===k?"btn-gold":"btn-ghost"}`}>{v}</button>
              ))}
            </div>
          </div>
          <div className="rounded bg-black/40 border border-white/10 p-3 text-xs text-white/50">
            Balance: <span className="text-[#d4af37] font-bold">{fmt(currency==="HUF"?user?.balance_huf:user?.balance_eur, currency)}</span>
          </div>
          {isRunning ? (
            <button data-testid="dragon-cashout" onClick={cashout} disabled={busy || round.current_level === 0}
                    className="w-full btn-gold px-6 py-4 rounded-md text-lg">
              Cash Out · {round.multiplier.toFixed(2)}x
            </button>
          ) : (
            <button data-testid="dragon-start" onClick={start} disabled={busy}
                    className="w-full btn-burgundy px-6 py-4 rounded-md text-lg uppercase tracking-widest">
              🔥 Enter Tower
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
